export type QueryValue = string | string[] | undefined;

type NumberQueryOptions = {
  integer?: boolean;
  min?: number;
};

function isSentinelQueryValue(value: string): boolean {
  return value === 'null' || value === 'undefined';
}

/**
 * Parse a single Next.js query/route value to a trimmed string, or null.
 * Arrays are rejected (return null) so repeated params cannot silently pick
 * the first value. Sentinel strings "null" / "undefined" are treated as missing.
 */
export function parseStringQueryParameter(value: QueryValue): string | null {
  if (value === undefined || Array.isArray(value)) {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed || isSentinelQueryValue(trimmed)) {
    return null;
  }

  return trimmed;
}

/**
 * Parse a Next.js query/route value to a finite number, or null.
 * Unlike parseInt, trailing junk (e.g. "12abc") is rejected.
 */
export function parseNumberQueryParameter(
  value: QueryValue,
  { integer, min }: NumberQueryOptions = {}
): number | null {
  const stringValue = parseStringQueryParameter(value);
  if (stringValue === null) {
    return null;
  }

  const parsed = Number(stringValue);
  if (!Number.isFinite(parsed)) {
    return null;
  }

  if (integer && !Number.isInteger(parsed)) {
    return null;
  }

  if (min !== undefined && parsed < min) {
    return null;
  }

  return parsed;
}

/**
 * Parse a Next.js query/route value to a boolean ("true" / "false", case-insensitive).
 */
export function parseBooleanQueryParameter(value: QueryValue): boolean | null {
  const stringValue = parseStringQueryParameter(value);
  if (stringValue === null) {
    return null;
  }

  const normalized = stringValue.toLowerCase();
  if (normalized === 'true') {
    return true;
  }
  if (normalized === 'false') {
    return false;
  }

  return null;
}

/**
 * Parse a Next.js query/route value and require it to be one of the allowed values.
 */
export function parseEnumQueryParameter<T extends string>(
  value: QueryValue,
  allowed: readonly T[]
): T | null {
  const stringValue = parseStringQueryParameter(value);
  if (stringValue === null) {
    return null;
  }

  if ((allowed as readonly string[]).includes(stringValue)) {
    return stringValue as T;
  }

  return null;
}

/**
 * Parse a Next.js query/route value to a string array.
 * Accepts a single string or an array (because ?ids=a arrives as a string).
 * Blank and sentinel entries are dropped; an empty result returns null.
 */
export function parseStringArrayQueryParameter(
  value: QueryValue
): string[] | null {
  if (value === undefined) {
    return null;
  }

  const entries = Array.isArray(value) ? value : [value];
  const trimmed = entries
    .map((item) => item.trim())
    .filter((item) => item.length > 0 && !isSentinelQueryValue(item));

  if (trimmed.length === 0) {
    return null;
  }

  return trimmed;
}
